import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import * as commentsApi from '../../api/comments';
import type { Comment, CommentPage } from '../../types/comments';
import { commentKeys } from './commentKeys';

const PAGE_SIZE = 20;

export interface UseCommentRepliesResult {
  replies: Comment[];
  isLoading: boolean;
  isError: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/**
 * Replies for a single top-level comment. Lazy — caller toggles `enabled`
 * once the user opens the thread to avoid fetching unread sub-trees.
 */
export function useCommentReplies(
  parentId: string,
  enabled: boolean
): UseCommentRepliesResult {
  const query = useInfiniteQuery<CommentPage>({
    queryKey: commentKeys.replies(parentId),
    queryFn: ({ pageParam }) =>
      commentsApi.listReplies(parentId, {
        after: typeof pageParam === 'string' ? pageParam : undefined,
        limit: PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });

  const flat = ((query.data as InfiniteData<CommentPage> | undefined)?.pages ?? []).flatMap(
    (p) => p.comments
  );

  return {
    replies: flat,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage();
    },
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
